/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import type { VNode } from "preact";
import { useEffect, useState } from "preact/hooks";
import { Route, Router, Switch, Redirect, useLocation } from "wouter-preact";
import {
  AccessToken,
  Amounts,
  HttpStatusCode,
  MerchantAuthMethod,
  TalerMerchantApi,
} from "@gnu-taler/taler-util";
import { useHashLocation } from "./routing/useHashLocation.js";
import { useQueryParams } from "./routing/useQueryParams.js";
import { Layout } from "./ui/Layout.js";
import { OrderListRoute } from "./routes/OrderListRoute.js";
import type { PayoutAccount } from "./screens/MoneyInScreen.js";
import { lazy, Suspense } from "preact/compat";
import { MoneyInRoute } from "./routes/MoneyInRoute.js";
import { GuidedSetupScreen } from "./screens/GuidedSetupScreen.js";
import { SelfProvisionRoute } from "./routes/SelfProvisionRoute.js";
import { AddPayoutAccountScreen } from "./screens/AddPayoutAccountScreen.js";
import { SignInRoute } from "./routes/SignInRoute.js";
import { ForgotPasswordScreen } from "./screens/ForgotPasswordScreen.js";
import { ChangeServerUrlRoute } from "./routes/ChangeServerUrlRoute.js";
import { OrderDetailRoute } from "./routes/OrderDetailRoute.js";
import { OrderRefundRoute } from "./routes/OrderRefundRoute.js";
import { CreateOrderRoute } from "./routes/CreateOrderRoute.js";
import { BusinessSettingsRoute } from "./routes/BusinessSettingsRoute.js";
import { PersonalizationScreen } from "./screens/PersonalizationScreen.js";
import { InventoryRoute } from "./routes/InventoryRoute.js";
import { CreateProductRoute } from "./routes/CreateProductRoute.js";
import { TemplatesRoute } from "./routes/TemplatesRoute.js";
import { CreateTemplateRoute } from "./routes/CreateTemplateRoute.js";
import { TemplateDetailsRoute } from "./routes/TemplateDetailsRoute.js";
import { AccessRoute } from "./routes/AccessRoute.js";
import { CreateAccessRoute } from "./routes/CreateAccessRoute.js";
import { StatisticsRoute } from "./routes/StatisticsRoute.js";
import { ReportsRoute } from "./routes/ReportsRoute.js";
import { WebhooksRoute } from "./routes/WebhooksRoute.js";
import { CreateWebhookRoute } from "./routes/CreateWebhookRoute.js";
import { SubscriptionsRoute } from "./routes/SubscriptionsRoute.js";
import { CreateSubscriptionRoute } from "./routes/CreateSubscriptionRoute.js";
import { PaymentServicesRoute } from "./routes/PaymentServicesRoute.js";
import { DevicesRoute } from "./routes/DevicesRoute.js";
import { CreateDeviceRoute } from "./routes/CreateDeviceRoute.js";
import { ServerInfoRoute } from "./routes/ServerInfoRoute.js";
import { KycAuthInstructionsRoute } from "./routes/KycAuthInstructionsRoute.js";
import { MfaChallengeRoute } from "./routes/MfaChallengeRoute.js";
import { PosRoute } from "./routes/PosRoute.js";
import type { IssuedPairingCredential } from "@gnu-taler/taler-util";
import { UnauthorizedScreen } from "./screens/UnauthorizedScreen.js";
import { NotFoundScreen } from "./screens/NotFoundScreen.js";
import { ScheduleReportRoute } from "./routes/ScheduleReportRoute.js";
import { ErrorInjectionTestScreen } from "./screens/ErrorInjectionTestScreen.js";
import { BootstrapInstanceRoute } from "./routes/BootstrapInstanceRoute.js";
import { ApiErrorBanner } from "./ui/ApiErrorBanner.js";
import { FooterControls } from "./ui/FooterControls.js";
import { TalerLogo } from "./ui/TalerLogo.js";
import { decodeRouteSegment } from "./routing/resourcePath.js";
import {
  AdminAccountCreateRoute,
  AdminAccountCredentialsRoute,
  AdminAccountDetailRoute,
  AdminAccountEditRoute,
  AdminAccountsRoute,
} from "./routes/AdminAccountsRoutes.js";

const StoryViewerScreen = lazy(async () => {
  const m = await import("./stories/StoryViewerScreen.js");
  return { default: m.StoryViewerScreen };
});

const DevSettingsScreen = lazy(async () => {
  const m = await import("./screens/DevSettingsScreen.js");
  return { default: m.DevSettingsScreen };
});

const InteractiveTutorialScreen = lazy(async () => {
  const m = await import("./screens/InteractiveTutorialScreen.js");
  return { default: m.InteractiveTutorialScreen };
});

function withRouteId(
  raw: string | undefined,
  render: (id: string) => VNode,
): VNode {
  const id = decodeRouteSegment(raw);
  return id === undefined ? <NotFoundScreen /> : render(id);
}

function parseKycLimits(
  raw: string,
): NonNullable<TalerMerchantApi.MerchantAccountKycRedirect["limits"]> {
  const value: unknown = JSON.parse(raw);
  const allowedOperations = new Set([
    "WITHDRAW",
    "DEPOSIT",
    "MERGE",
    "AGGREGATE",
    "BALANCE",
    "REFUND",
    "CLOSE",
    "TRANSACTION",
  ]);
  if (
    !Array.isArray(value) ||
    value.some((entry) => {
      if (!entry || typeof entry !== "object" || Array.isArray(entry))
        return true;
      const item = entry as Record<string, unknown>;
      const timeframe = item.timeframe;
      if (
        !timeframe ||
        typeof timeframe !== "object" ||
        Array.isArray(timeframe)
      )
        return true;
      const duration = (timeframe as Record<string, unknown>).d_us;
      return (
        typeof item.operation_type !== "string" ||
        !allowedOperations.has(item.operation_type) ||
        typeof item.threshold !== "string" ||
        !Amounts.checkString(item.threshold) ||
        (duration !== "forever" &&
          (typeof duration !== "number" ||
            !Number.isFinite(duration) ||
            duration < 0))
      );
    })
  )
    throw new Error("KYC limits have an invalid shape");
  return value as NonNullable<
    TalerMerchantApi.MerchantAccountKycRedirect["limits"]
  >;
}

export const FORGOT_PASSWORD_MFA_ROUTE = "/forgot-password/mfa";

const PUBLIC_ROUTES = [
  "/signin",
  "/signup",
  "/forgot-password",
  "/change-server-url",
  "/dev",
  "/tutorial",
];

export function isPublicPortalRoute(location: string): boolean {
  return PUBLIC_ROUTES.some(
    (route) => location === route || location.startsWith(`${route}/`),
  );
}
import { useTranslation } from "./context/translation.js";
import { normalizeApiFailure } from "./api/failure.js";
import { merchantClient } from "./api/client.js";
import { unwrap } from "./api/failure.js";
import { runProtectedMutation } from "./api/contracts.js";
import type { PendingProtectedAction } from "./api/protectedAction.js";
import {
  signIn,
  signOut,
  session,
  currentBackendUrl,
  currentAccount,
  unauthorizedError,
  clearUnauthorizedError,
  consumeSignInReturnPath,
  lastMerchantAccountStore,
  customBackendUrl,
  type LoginTokenInfo,
  type SessionIdentity,
  captureSessionIdentity,
  isCurrentSessionIdentity,
} from "./stores/session.js";
import { webUiConfigStatus } from "./stores/webuiConfig.js";
import { useLoginTokenRefresh } from "./api/tokenRefresh.js";
import {
  usePayoutAccounts,
  useBusinessSettings,
  useOrders,
  useTemplates,
  useManagedInstances,
  useBootstrapInstance,
  useMerchantConfig,
} from "./api/hooks.js";

export function AppContent(): VNode {
  const { t, lang } = useTranslation();
  const [location, setLocation] = useLocation();
  const searchParams = useQueryParams();
  const hasToken = !!session.value.token;
  const requiresServerChoice =
    (webUiConfigStatus.value === "missing" ||
      webUiConfigStatus.value === "invalid") &&
    !customBackendUrl.value.trim() &&
    !session.value.backendBaseUrl;
  const needsMoneyData = location === "/setup" || location.startsWith("/money");
  const needsAdminData = location.startsWith("/admin");
  const bootstrap = useBootstrapInstance(!hasToken);

  useEffect(() => {
    document.title = t`Taler Merchant Portal`;
  }, [lang, t]);

  // Live SWR data hooks for layout, setup, and MFA handlers
  const {
    accounts: realAccounts,
    isLoading: accountsLoading,
    resource: accountsResource,
    createPayoutAccount,
    deletePayoutAccount,
  } = usePayoutAccounts({ enabled: needsMoneyData });
  const merchantConfig = useMerchantConfig();
  const {
    settings: realBusinessSettings,
    isLoading: businessSettingsLoading,
    resource: businessSettingsResource,
  } = useBusinessSettings({ enabled: location === "/settings/account" });
  const { orders: firstOrders, isLoading: firstOrdersLoading } = useOrders({
    enabled: needsMoneyData,
    delta: 1,
    live: false,
  });
  const { templates: firstTemplates, isLoading: firstTemplatesLoading } =
    useTemplates({ enabled: needsMoneyData });
  const adminAccess = useManagedInstances({ enabled: needsAdminData });
  const gateAdminRoute = (
    content: VNode,
    additionallyAllowed = true,
  ): VNode => {
    if (adminAccess.isAdminIdentity && adminAccess.resource.isLoading) {
      return (
        <div class="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">{t`Checking administrator access…`}</div>
      );
    }
    return adminAccess.hasManagementAccess && additionallyAllowed ? (
      content
    ) : (
      <Redirect to="/admin/accounts" />
    );
  };
  const hasOrders = Boolean(firstOrders?.length);
  const hasTemplates = Boolean(firstTemplates?.length);

  const [pendingMfa, setPendingMfa] = useState<PendingProtectedAction | null>(
    null,
  );
  const beginPendingMfa = (pending: PendingProtectedAction): void => {
    setPendingMfa({
      ...pending,
      identity: pending.identity ?? captureSessionIdentity(),
    });
  };
  useEffect(() => {
    if (
      pendingMfa?.identity &&
      !isCurrentSessionIdentity(pendingMfa.identity)
    ) {
      setPendingMfa(null);
    }
    // Session and backend identity live in signals read by the helper above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pendingMfa,
    session.value.token,
    session.value.account,
    currentBackendUrl.value,
  ]);
  // Pairing credentials are one-time secrets. Keep the issued value only in
  // memory so an MFA redirect can hand it back to the access screen without
  // putting it in browser history or persistent storage.
  const [issuedPairingCredential, setIssuedPairingCredential] =
    useState<IssuedPairingCredential | null>(null);
  const pairingAccount = currentAccount.value;
  const pairingBackend = currentBackendUrl.value;
  useEffect(() => {
    setIssuedPairingCredential(null);
  }, [pairingAccount, pairingBackend]);

  // Hand the one-time machine token across the MFA route in memory only.
  // Bind it to the initiating session, including while issuance is in flight.
  const creationIdentity = captureSessionIdentity();
  const [issuedAccessToken, setIssuedAccessToken] = useState<{
    token: string;
    identity: SessionIdentity;
  } | null>(null);
  const accessTokenIsCurrent =
    issuedAccessToken !== null &&
    isCurrentSessionIdentity(issuedAccessToken.identity);
  const inAccessCreationFlow =
    location === "/access/new" || location === "/money/payout-accounts/mfa";
  useEffect(() => {
    if (!accessTokenIsCurrent || !inAccessCreationFlow) {
      setIssuedAccessToken(null);
    }
  }, [accessTokenIsCurrent, inAccessCreationFlow]);

  const handleSignIn = (data: {
    account: string;
    token: string;
    backendUrl?: string;
    tokenInfo?: LoginTokenInfo;
  }) => {
    signIn(
      data.account,
      data.token as AccessToken,
      data.backendUrl,
      data.tokenInfo,
    );
    setLocation(consumeSignInReturnPath() || "/orders");
  };

  // Renew the login token before it lapses, for as long as the portal is open.
  useLoginTokenRefresh();

  const isPublicRoute = isPublicPortalRoute(location);
  useEffect(() => {
    if (
      requiresServerChoice &&
      location !== "/change-server-url" &&
      location !== "/dev" &&
      !location.startsWith("/tutorial")
    ) {
      setLocation("/change-server-url");
    }
  }, [requiresServerChoice, location, setLocation]);

  // Unauthenticated route guard: redirect to sign-in unless visiting a public page.
  useEffect(() => {
    if (!hasToken && !isPublicRoute && !requiresServerChoice) {
      setLocation("/signin");
    }
  }, [location, hasToken, isPublicRoute, requiresServerChoice, setLocation]);

  // Render UnauthorizedScreen if session token was rejected by server (401 Unauthorized)
  if (unauthorizedError.value) {
    return (
      <UnauthorizedScreen
        accountName={unauthorizedError.value.account}
        serverUrl={currentBackendUrl.value}
        errorDetail={unauthorizedError.value.detail}
        onSignInAgain={() => {
          clearUnauthorizedError();
          setLocation("/signin");
        }}
      />
    );
  }

  // Standalone Web PoS Terminal Route (requires authenticated session from localStorage)
  if (location.startsWith("/pos") && hasToken) {
    let subRoute: "catalog" | "amount" | "pay" | "success" | "history" =
      "catalog";
    let orderId: string | undefined;

    if (location === "/pos/amount") subRoute = "amount";
    else if (location === "/pos/history") subRoute = "history";
    else if (location.startsWith("/pos/pay/")) {
      subRoute = "pay";
      orderId = decodeRouteSegment(
        location.replace("/pos/pay/", "").split("?")[0],
      );
    } else if (location.startsWith("/pos/success/")) {
      subRoute = "success";
      orderId = decodeRouteSegment(
        location.replace("/pos/success/", "").split("?")[0],
      );
    }

    return <PosRoute initialSubRoute={subRoute} activeOrderId={orderId} />;
  }

  if (location === "/change-server-url") {
    return (
      <ChangeServerUrlRoute
        initialUrl={currentBackendUrl.value}
        onSave={() => setLocation("/signin")}
        onCancel={() => setLocation("/signin")}
      />
    );
  }

  if (!hasToken && bootstrap.state.type === "loading") {
    return (
      <div class="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative">
        <FooterControls />
        <div class="sm:mx-auto sm:w-full sm:max-w-md text-center">
          <TalerLogo class="h-12 w-auto mx-auto mb-2" color="#0042b3" />
          <h1 class="text-2xl font-extrabold text-gray-900">Taler</h1>
          <div class="mt-6 rounded-lg border border-gray-200 bg-white p-8 text-sm text-gray-500 shadow">
            {t`Checking whether this merchant server needs initial setup...`}
          </div>
        </div>
      </div>
    );
  }

  if (!hasToken && bootstrap.state.type === "error") {
    return (
      <div class="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative">
        <FooterControls />
        <div class="sm:mx-auto sm:w-full sm:max-w-md text-center">
          <TalerLogo class="h-12 w-auto mx-auto mb-2" color="#0042b3" />
          <h1 class="text-2xl font-extrabold text-gray-900">Taler</h1>
          <div class="mt-6 bg-white py-8 px-4 shadow rounded-lg sm:px-10 border border-gray-200 text-left">
            <ApiErrorBanner
              error={bootstrap.state.error}
              title={t`Could not inspect this merchant server`}
              actionLabel={t`Try again`}
              onAction={() => void bootstrap.refresh()}
            />
            <button
              type="button"
              onClick={() => setLocation("/change-server-url")}
              class="mt-4 w-full text-center text-xs font-semibold text-taler-brand hover:underline"
            >
              {t`Change server address`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!hasToken && bootstrap.state.type === "required") {
    return (
      <BootstrapInstanceRoute
        backendUrl={currentBackendUrl.value}
        onChangeServer={() => setLocation("/change-server-url")}
        createFirstInstance={bootstrap.createFirstInstance}
        onCreated={(data) => {
          signIn(data.account, data.token, data.backendUrl, data.tokenInfo);
          setLocation("/setup");
        }}
      />
    );
  }

  if (location === FORGOT_PASSWORD_MFA_ROUTE) {
    if (!pendingMfa) {
      return <Redirect to="/forgot-password" />;
    }
    return (
      <MfaChallengeRoute
        action={pendingMfa}
        onComplete={(target) => {
          setPendingMfa(null);
          setLocation(target);
        }}
        onCancel={() => {
          const target = pendingMfa.cancelTo;
          setPendingMfa(null);
          setLocation(target);
        }}
      />
    );
  }

  if (location === "/forgot-password") {
    const accountParam = searchParams.get("account") || undefined;
    return (
      <ForgotPasswordScreen
        initialAccount={accountParam}
        initialBackendUrl={currentBackendUrl.value}
        onRequestReset={async (data) => {
          const rootUrl = new URL(data.backendUrl);
          const client = merchantClient({ rootUrl, account: data.account });
          const res = await client.forgotPasswordSelfProvision({
            method: MerchantAuthMethod.TOKEN,
            password: data.newPassword,
          });

          if (res.type === "fail") {
            const resCase = res.case;
            if (resCase === HttpStatusCode.Accepted) {
              beginPendingMfa({
                accountLabel: data.account,
                challengeAccount: data.account,
                actionNotice: t`Resetting forgotten password for merchant account (${data.account})`,
                challengeResponse:
                  res.body as TalerMerchantApi.ChallengeResponse,
                cancelTo: `/forgot-password?account=${encodeURIComponent(data.account)}`,
                continueWith: (challengeIds) =>
                  runProtectedMutation(async () => {
                    const continued = await client.forgotPasswordSelfProvision(
                      {
                        method: MerchantAuthMethod.TOKEN,
                        password: data.newPassword,
                      },
                      { challengeIds },
                    );
                    if (
                      continued.type === "fail" &&
                      continued.case === HttpStatusCode.Accepted
                    ) {
                      return {
                        type: "challenge" as const,
                        challenge:
                          continued.body as TalerMerchantApi.ChallengeResponse,
                      };
                    }
                    unwrap(continued);
                    lastMerchantAccountStore.set(data.account.toLowerCase());
                    return {
                      redirectTo: `/signin?account=${encodeURIComponent(data.account)}&password_reset=1`,
                    };
                  }),
              });
              setLocation(FORGOT_PASSWORD_MFA_ROUTE);
              return { mfaRequired: true };
            }
            if (resCase === HttpStatusCode.NotFound) {
              return {
                errorMsg: t`There is no merchant account called "${data.account}" on this server.`,
                error: normalizeApiFailure(res),
              };
            }
            if (resCase === HttpStatusCode.Forbidden) {
              return {
                errorMsg: t`This merchant account has no e-mail address or phone number set, so its password cannot be reset here. Contact your provider.`,
                error: normalizeApiFailure(res),
              };
            }
            const detail = res.detail as { hint?: string } | undefined;
            return {
              errorMsg:
                detail?.hint || t`Failed to process password reset request.`,
              error: normalizeApiFailure(res),
            };
          }

          lastMerchantAccountStore.set(data.account.toLowerCase());
          setLocation(
            `/signin?account=${encodeURIComponent(data.account)}&password_reset=1`,
          );
          return { ok: true };
        }}
      />
    );
  }

  if (location === "/signin" || (!hasToken && !isPublicRoute)) {
    return (
      <SignInRoute
        initialBackendUrl={currentBackendUrl.value}
        initialAccount={searchParams.get("account") || undefined}
        notice={
          searchParams.get("password_reset") === "1"
            ? t`Your password was reset. Sign in with your new password.`
            : undefined
        }
        onCancel={
          searchParams.get("from_admin") === "1"
            ? () => setLocation("/admin/accounts")
            : undefined
        }
        onSignIn={handleSignIn}
      />
    );
  }

  if (location === "/signup") {
    return (
      <SelfProvisionRoute
        initialBackendUrl={currentBackendUrl.value}
        onAccountCreated={(data) => {
          if (!data.token) {
            // Created, but the backend wants a second factor before it issues a
            // token. Sign-in is the screen built for that.
            lastMerchantAccountStore.set(data.account);
            setLocation("/signin");
            return;
          }
          signIn(
            data.account,
            data.token as AccessToken,
            data.backendUrl,
            data.tokenInfo,
          );
          setLocation("/setup");
        }}
      />
    );
  }

  if (location === "/dev") {
    return (
      <Suspense
        fallback={
          <div class="p-8 text-center text-gray-500 font-bold">
            {t`Loading dev settings...`}
          </div>
        }
      >
        <DevSettingsScreen />
      </Suspense>
    );
  }

  const kycPendingAccount =
    hasToken && realAccounts
      ? realAccounts.find(
          (acc: PayoutAccount) => acc.status === "action_needed",
        )
      : undefined;
  const actionNeededCount =
    hasToken && realAccounts
      ? realAccounts.filter(
          (acc: PayoutAccount) => acc.status === "action_needed",
        ).length
      : 0;
  const kycWarningBanner =
    kycPendingAccount && !location.startsWith("/money/payout-accounts")
      ? t`Your payment service needs to check your identity before it can pay into your bank account (${kycPendingAccount.owner}).`
      : undefined;

  return (
    <Layout
      serverUrl={currentBackendUrl.value}
      accountName={currentAccount.value || "default"}
      actionNeededCount={actionNeededCount}
      kycWarningBanner={kycWarningBanner}
      onKycAction={() => {
        setLocation("/money/payout-accounts");
      }}
      onSignOut={() => {
        signOut();
        setLocation("/signin");
      }}
      showAdministration={adminAccess.isAdminIdentity}
    >
      <Switch>
        <Route path="/">
          <OrderListRoute onCreateOrder={() => setLocation("/orders/new")} />
        </Route>

        <Route path="/guide">
          <Redirect to="/setup" />
        </Route>
        <Route path="/setup">
          <GuidedSetupScreen
            businessName={realBusinessSettings?.name}
            businessAddress={
              realBusinessSettings?.addressLocation
                ? `${realBusinessSettings.addressLocation.street || ""} ${realBusinessSettings.addressLocation.town || ""}`.trim()
                : undefined
            }
            businessLogo={realBusinessSettings?.logoUrl}
            accounts={realAccounts}
            accountsResource={accountsResource}
            businessSettingsResource={businessSettingsResource}
            isLoading={businessSettingsLoading || accountsLoading}
            accountName={session.value.account}
            hasOrders={hasOrders}
            hasTemplates={hasTemplates}
          />
        </Route>
        <Route path="/orders">
          <OrderListRoute onCreateOrder={() => setLocation("/orders/new")} />
        </Route>
        <Route path="/orders/new">
          <CreateOrderRoute />
        </Route>
        <Route path="/orders/:id/refund">
          {(params) =>
            withRouteId(params.id, (id) => <OrderRefundRoute orderId={id} />)
          }
        </Route>
        <Route path="/orders/:id">
          {(params) =>
            withRouteId(params.id, (id) => <OrderDetailRoute orderId={id} />)
          }
        </Route>
        <Route path="/inventory/new">
          <CreateProductRoute />
        </Route>
        <Route path="/inventory/:id/edit">
          {(params) =>
            withRouteId(params.id, (id) => <CreateProductRoute editId={id} />)
          }
        </Route>
        <Route path="/inventory">
          <InventoryRoute />
        </Route>
        <Route path="/templates/new">
          <CreateTemplateRoute />
        </Route>
        <Route path="/templates/:id/edit">
          {(params) =>
            withRouteId(params.id, (id) => <CreateTemplateRoute editId={id} />)
          }
        </Route>
        <Route path="/templates/:id">
          {(params) =>
            withRouteId(params.id, (id) => (
              <TemplateDetailsRoute templateId={id} />
            ))
          }
        </Route>
        <Route path="/templates">
          <TemplatesRoute />
        </Route>
        <Route path="/access/new">
          <CreateAccessRoute
            issuedToken={accessTokenIsCurrent ? issuedAccessToken.token : null}
            onIssuedTokenChange={(token) => {
              if (token === null) {
                setIssuedAccessToken(null);
              } else if (
                creationIdentity &&
                isCurrentSessionIdentity(creationIdentity)
              ) {
                setIssuedAccessToken({ token, identity: creationIdentity });
              }
            }}
            onMfaRequired={(pending) => {
              beginPendingMfa(pending);
              setLocation("/money/payout-accounts/mfa");
            }}
          />
        </Route>
        <Route path="/access">
          <AccessRoute
            issuedPairingCredential={issuedPairingCredential}
            onIssuedPairingCredentialChange={setIssuedPairingCredential}
            onMfaRequired={(pending) => {
              beginPendingMfa(pending);
              setLocation("/money/payout-accounts/mfa");
            }}
          />
        </Route>
        <Route path="/admin/accounts/new">
          {gateAdminRoute(
            <AdminAccountCreateRoute
              onMfaRequired={(pending) => {
                beginPendingMfa(pending);
                setLocation("/money/payout-accounts/mfa");
              }}
            />,
          )}
        </Route>
        <Route path="/admin/accounts/:id/credentials">
          {(params) =>
            withRouteId(params.id, (id) =>
              gateAdminRoute(
                <AdminAccountCredentialsRoute
                  instanceId={id}
                  onMfaRequired={(pending) => {
                    beginPendingMfa(pending);
                    setLocation("/money/payout-accounts/mfa");
                  }}
                />,
                id !== "admin",
              ),
            )
          }
        </Route>
        <Route path="/admin/accounts/:id/edit">
          {(params) =>
            withRouteId(params.id, (id) =>
              gateAdminRoute(
                <AdminAccountEditRoute
                  instanceId={id}
                  onMfaRequired={(pending) => {
                    beginPendingMfa(pending);
                    setLocation("/money/payout-accounts/mfa");
                  }}
                />,
              ),
            )
          }
        </Route>
        <Route path="/admin/accounts/:id/verification">
          {(params) =>
            withRouteId(params.id, (id) =>
              gateAdminRoute(
                <AdminAccountDetailRoute
                  instanceId={id}
                  tab="verification"
                  onSignIn={(nextId) =>
                    setLocation(
                      `/signin?account=${encodeURIComponent(nextId)}&from_admin=1`,
                    )
                  }
                />,
              ),
            )
          }
        </Route>
        <Route path="/admin/accounts/:id">
          {(params) =>
            withRouteId(params.id, (id) =>
              gateAdminRoute(
                <AdminAccountDetailRoute
                  instanceId={id}
                  onSignIn={(nextId) =>
                    setLocation(
                      `/signin?account=${encodeURIComponent(nextId)}&from_admin=1`,
                    )
                  }
                />,
              ),
            )
          }
        </Route>
        <Route path="/admin/accounts">
          {adminAccess.isAdminIdentity ? (
            <AdminAccountsRoute
              onMfaRequired={(pending) => {
                beginPendingMfa(pending);
                setLocation("/money/payout-accounts/mfa");
              }}
              onSignIn={(id) =>
                setLocation(
                  `/signin?account=${encodeURIComponent(id)}&from_admin=1`,
                )
              }
            />
          ) : (
            <NotFoundScreen />
          )}
        </Route>
        <Route path="/statistics">
          <StatisticsRoute />
        </Route>
        <Route path="/reports/new">
          <ScheduleReportRoute />
        </Route>
        <Route path="/reports/scheduled">
          <ReportsRoute initialTab="scheduled" />
        </Route>
        <Route path="/reports/groupings">
          <ReportsRoute initialTab="groupings" />
        </Route>
        <Route path="/reports">
          <Redirect to="/reports/scheduled" />
        </Route>
        <Route path="/settings/webhooks/new">
          <CreateWebhookRoute />
        </Route>
        <Route path="/settings/webhooks/:id/edit">
          {(params) =>
            withRouteId(params.id, (id) => <CreateWebhookRoute editId={id} />)
          }
        </Route>
        <Route path="/settings/webhooks">
          <WebhooksRoute />
        </Route>
        <Route path="/authenticators/new">
          <CreateDeviceRoute />
        </Route>
        <Route path="/authenticators/:id/edit">
          {(params) =>
            withRouteId(params.id, (id) => <CreateDeviceRoute editId={id} />)
          }
        </Route>
        <Route path="/authenticators">
          <DevicesRoute />
        </Route>
        <Route path="/settings/devices">
          <DevicesRoute />
        </Route>
        <Route path="/subscriptions/new">
          <CreateSubscriptionRoute />
        </Route>
        <Route path="/subscriptions/:id/edit">
          {(params) =>
            withRouteId(params.id, (id) => (
              <CreateSubscriptionRoute editId={id} />
            ))
          }
        </Route>
        <Route path="/subscriptions">
          <SubscriptionsRoute />
        </Route>
        <Route path="/settings/server">
          <ServerInfoRoute />
        </Route>
        <Route path="/settings/payment-services">
          <PaymentServicesRoute />
        </Route>
        <Route path="/settings/account">
          <BusinessSettingsRoute
            onMfaRequired={(pending) => {
              beginPendingMfa({
                accountLabel: currentAccount.value || t`Merchant Account`,
                ...pending,
              });
              setLocation("/money/payout-accounts/mfa");
            }}
          />
        </Route>
        <Route path="/personalization">
          <PersonalizationScreen />
        </Route>
        <Route path="/money/:section">
          {(params) =>
            params.section === "payout-accounts" ||
            params.section === "transfers" ? (
              <MoneyInRoute
                initialTab={
                  params.section === "transfers" ? "transfers" : "accounts"
                }
                accounts={realAccounts}
                accountsResource={accountsResource}
                isLoading={accountsLoading}
                token={session.value.token}
                hasOrders={hasOrders}
                hasTemplates={hasTemplates}
                isOrdersLoading={firstOrdersLoading}
                isTemplatesLoading={firstTemplatesLoading}
                onAddAccount={() => setLocation("/money/payout-accounts/add")}
                onReactivateAccount={async (acc) => {
                  if (createPayoutAccount) {
                    const accountData = {
                      paytoUri: acc.account,
                    };
                    const res = await createPayoutAccount(accountData);
                    if (res.mfaRequired && res.challengeResponse) {
                      beginPendingMfa({
                        accountLabel: acc.owner || acc.account,
                        actionNotice: t`Add a bank account`,
                        challengeResponse: res.challengeResponse,
                        cancelTo: "/money/payout-accounts",
                        continueWith: (solvedChallengeIds) =>
                          runProtectedMutation(async () => {
                            const continued = await createPayoutAccount(
                              accountData,
                              solvedChallengeIds,
                            );
                            if (
                              continued.mfaRequired &&
                              continued.challengeResponse
                            ) {
                              return {
                                type: "challenge" as const,
                                challenge: continued.challengeResponse,
                              };
                            }
                            return { redirectTo: "/money/payout-accounts" };
                          }),
                        revalidate: accountsResource.refresh,
                      });
                      setLocation("/money/payout-accounts/mfa");
                    }
                  }
                }}
                onDeleteAccount={async (acc, challengeIds) => {
                  if (acc.hWire && deletePayoutAccount) {
                    const res = await deletePayoutAccount(
                      acc.hWire,
                      challengeIds,
                    );
                    if (res && res.mfaRequired && res.challengeResponse) {
                      beginPendingMfa({
                        accountLabel: acc.owner || acc.account,
                        actionNotice: t`Deleting the bank account ${acc.owner || acc.account}`,
                        challengeResponse: res.challengeResponse,
                        cancelTo: "/money/payout-accounts",
                        continueWith: (solvedChallengeIds) =>
                          runProtectedMutation(async () => {
                            const continued = await deletePayoutAccount(
                              acc.hWire!,
                              solvedChallengeIds,
                            );
                            if (
                              continued.mfaRequired &&
                              continued.challengeResponse
                            ) {
                              return {
                                type: "challenge" as const,
                                challenge: continued.challengeResponse,
                              };
                            }
                            return { redirectTo: "/money/payout-accounts" };
                          }),
                        revalidate: accountsResource.refresh,
                      });
                      setLocation("/money/payout-accounts/mfa");
                    }
                    return res;
                  }
                  return { mfaRequired: false };
                }}
                onViewWireInstructions={({
                  hWire,
                  exchangeUrl,
                  limits,
                  kycSwapTosAcceptance,
                  tosAcceptedEarly,
                  senderAccount,
                }) => {
                  const limitsParam = limits
                    ? `&limits=${encodeURIComponent(JSON.stringify(limits))}`
                    : "";
                  const swapParam = kycSwapTosAcceptance
                    ? `&kyc_swap_tos=1`
                    : "";
                  const tosParam = tosAcceptedEarly
                    ? `&tos_accepted=${encodeURIComponent(tosAcceptedEarly)}`
                    : "";
                  const senderParam = senderAccount
                    ? `&sender_payto=${encodeURIComponent(senderAccount)}`
                    : "";
                  setLocation(
                    `/money/payout-accounts/wire-instructions?h_wire=${hWire}&exchange_url=${encodeURIComponent(exchangeUrl)}${limitsParam}${swapParam}${tosParam}${senderParam}`,
                  );
                }}
              />
            ) : (
              <NotFoundScreen />
            )
          }
        </Route>
        <Route path="/money/payout-accounts/mfa">
          {pendingMfa ? (
            <MfaChallengeRoute
              action={pendingMfa}
              onComplete={(target) => {
                setPendingMfa(null);
                setLocation(target);
              }}
              onCancel={() => {
                const target = pendingMfa.cancelTo;
                setPendingMfa(null);
                setLocation(target);
              }}
            />
          ) : (
            <Redirect to="/money/payout-accounts" />
          )}
        </Route>
        <Route path="/money/payout-accounts/add">
          <AddPayoutAccountScreen
            paymentTargetTypes={merchantConfig.paymentTargetTypes}
            paymentTargetRegex={merchantConfig.paymentTargetRegex}
            policyResource={merchantConfig.resource}
            onSave={async (data) => {
              if (hasToken && createPayoutAccount) {
                const accountData = {
                  paytoUri: data.paytoUri,
                  userReference: data.userReference,
                };
                const res = await createPayoutAccount(accountData);
                if (res.mfaRequired && res.challengeResponse) {
                  beginPendingMfa({
                    accountLabel: data.holder || data.paytoUri,
                    actionNotice: t`Add a bank account`,
                    challengeResponse: res.challengeResponse,
                    cancelTo: "/money/payout-accounts/add",
                    continueWith: (solvedChallengeIds) =>
                      runProtectedMutation(async () => {
                        const continued = await createPayoutAccount(
                          accountData,
                          solvedChallengeIds,
                        );
                        if (
                          continued.mfaRequired &&
                          continued.challengeResponse
                        ) {
                          return {
                            type: "challenge" as const,
                            challenge: continued.challengeResponse,
                          };
                        }
                        return { redirectTo: "/money/payout-accounts" };
                      }),
                    revalidate: accountsResource.refresh,
                  });
                  setLocation("/money/payout-accounts/mfa");
                  return;
                }
              }
              setLocation("/money/payout-accounts");
            }}
            onCancel={() => setLocation("/money/payout-accounts")}
          />
        </Route>
        <Route path="/money/payout-accounts/wire-instructions">
          {() => {
            const rawLimits = searchParams.get("limits");
            let limits:
              | NonNullable<
                  TalerMerchantApi.MerchantAccountKycRedirect["limits"]
                >
              | undefined;
            try {
              limits = rawLimits ? parseKycLimits(rawLimits) : undefined;
            } catch {
              limits = undefined;
            }
            const swapTos = searchParams.get("kyc_swap_tos") === "1";
            const tosAcceptedEarly =
              searchParams.get("tos_accepted") || undefined;
            return (
              <KycAuthInstructionsRoute
                token={session.value.token}
                hWire={searchParams.get("h_wire") || undefined}
                exchangeUrl={searchParams.get("exchange_url") || undefined}
                senderAccount={searchParams.get("sender_payto") || undefined}
                limits={limits}
                kycSwapTosAcceptance={swapTos}
                tosAcceptedEarly={tosAcceptedEarly}
                onBack={() => setLocation("/money/payout-accounts")}
              />
            );
          }}
        </Route>
        <Route path="/dev">
          <DevSettingsScreen />
        </Route>
        <Route>
          <NotFoundScreen />
        </Route>
      </Switch>
    </Layout>
  );
}

import { TranslationProvider } from "./context/translation.js";

/**
 * Keep the error-injection control panel outside AppContent.  AppContent owns
 * data hooks for the normal portal shell, and mounting it just to reach the
 * control page would itself create requests which the panel then observes.
 */
function AppRoute(): VNode {
  const [location, setLocation] = useLocation();
  const { t } = useTranslation();
  if (location === "/errtest") return <ErrorInjectionTestScreen />;
  if (
    location === "/storybook" ||
    location.startsWith("/storybook/") ||
    location === "/stories" ||
    location.startsWith("/stories/")
  ) {
    return (
      <Suspense
        fallback={
          <div class="min-h-screen p-8 text-center text-gray-500 font-bold">
            {t`Loading Storybook...`}
          </div>
        }
      >
        <StoryViewerScreen />
      </Suspense>
    );
  }
  if (location.startsWith("/tutorial")) {
    const hasToken = !!session.value.token;
    return (
      <Suspense
        fallback={
          <div class="p-8 text-center text-gray-500 font-bold">
            {t`Loading tutorial...`}
          </div>
        }
      >
        <InteractiveTutorialScreen
          onExit={() => setLocation(hasToken ? "/orders" : "/signin")}
        />
      </Suspense>
    );
  }
  return <AppContent />;
}

export function App(): VNode {
  return (
    <TranslationProvider>
      <Router hook={useHashLocation}>
        <AppRoute />
      </Router>
    </TranslationProvider>
  );
}
